/**
 * Generate Jig stage-assembly module.
 * Combines built model data with generated stage instructions to produce the final
 * Jig payload shape sent back to the API.
 */
import type { BuiltModelData, StageInstructionWithTools } from './generate-jig.types'
import { deepClone, generateShortUid } from './generate-jig.utils'
import { applyToolsToStage } from './generate-jig.stage-tools'
import { buildFallbackStageInstructions, createDefaultStage } from './generate-jig.ai-prompts'

/**
 * Clones the copied Jig template, replaces its model payload, and expands the
 * normalized stage instruction list into a full set of serialized stages.
 */
export function applyModelAndStagesToJig(
  baseJig: any,
  modelData: BuiltModelData,
  stageInstructions: StageInstructionWithTools[],
  userQuestion: string
): any {
  const jig = deepClone(baseJig)

  if (!jig.JigMetaData) {
    jig.JigMetaData = {}
  }

  const trimmedQuestion = userQuestion ? userQuestion.trim() : ''
  jig.JigMetaData.ProjectDescription = trimmedQuestion.length > 0 ? trimmedQuestion : ''

  if (!jig.JigModelData) {
    jig.JigModelData = {}
  }

  jig.JigModelData.Models = [modelData.modelEntry]
  jig.JigModelData.Meshes = modelData.meshes
  jig.JigModelData.HasTexture = modelData.hasTexture
  jig.JigModelData.Textures = deepClone(modelData.textures)

  if (!Array.isArray(jig.JigModelData.Materials)) {
    jig.JigModelData.Materials = []
  }

  if (!jig.JigStageData) {
    jig.JigStageData = { Stages: [] }
  }

  if (!Array.isArray(jig.JigStageData.Stages) || jig.JigStageData.Stages.length === 0) {
    jig.JigStageData.Stages = [createDefaultStage(modelData.serializedElements)]
  }

  const templateStage = deepClone(jig.JigStageData.Stages[0])
  // Remove SerializedElements from the template to avoid cloning them N times as part of the full stage.
  // Each stage gets its own fresh clone of the model's serializedElements instead.
  const { SerializedElements: _discarded, ...templateStageWithoutElements } = templateStage

  const normalizedStages = stageInstructions.length > 0 ? stageInstructions : buildFallbackStageInstructions(userQuestion)

  const stages = normalizedStages.map((stageData: StageInstructionWithTools, index: number) => {
    const stage = {
      ...deepClone(templateStageWithoutElements),
      SerializedElements: deepClone(modelData.serializedElements),
      StageInstructions: stageData.instruction,
      StageOrder: index,
      UID: generateShortUid(),
      StageUid: generateShortUid(),
    }

    applyToolsToStage(stage, stageData, modelData.sceneContext)

    return stage
  })

  jig.JigStageData.Stages = stages

  return jig
}
